// What the kiosk admin is told about the venue and the show, taken from the
// activation rather than from a form.
//
// xp/KIOSK-ADMIN-REPORT.md section 8 calls this "the single most valuable thing
// on this list, because it is what makes every scheduling refusal true rather
// than merely consistent". Section 5 is the argument: `refusals()` refuses an
// item windowed for hours the hall is empty, and refuses a stretch of the
// opening hours with nothing eligible, and both judgements are only as good as
// the four strings they are made against. A show typed in a week out produces a
// set of refusals that are all internally consistent and all about the wrong
// week, and nothing on the screen would say so.
//
// api/migrations/108 put those facts in the database and
// operator/src/activations.ts already carries every one of them on a directory
// row: `time_zone`, `venue`, `opens_on`, `closes_on` and `opening_hours`. So
// this file is a reading rather than a form, and there is no text box on the
// kiosk screen for any of it.
//
// ---------------------------------------------------------------------------
// WHAT THIS FILE IS ALLOWED TO DO, WHICH IS NARROWER THAN IT LOOKS
//
// It may read the activation, walk the run with the rules module's own
// `showDays`, and compare strings for equality. It may not decide anything
// about a schedule, may not format a clock reading, and may not invent a time
// that the activation did not state. Every `HH:MM` and every `YYYY-MM-DD` that
// leaves here came out of the server's answer verbatim, and check.mjs asserts
// exactly that by searching the answer for each of them — the same observation
// the activations list already makes about numbers on a row.
//
// The reason for the rule is the one the whole kiosk split is built on. A
// console that worked out the venue's hours for itself would be a second
// opinion about when the hall is open, sitting one function call away from the
// refusals that are judged against it, and the two would disagree on the day
// somebody entered a lunch break.
//
// ---------------------------------------------------------------------------
// THREE STATES FOR THE HOURS, AND THE THIRD IS THE LOAD-BEARING ONE
//
// 108 is explicit that a day with no row is not a day the stand is shut:
//
//   "If 'no row' meant 'shut', then a brand new activation that nobody has
//   touched is a show that is shut every day, the opening hours are empty,
//   `gapsIn` has nothing to find gaps inside, and every schedule published
//   against it is approved without objection. It is green because of what it
//   never looked at."
//
// On staging today every one of these columns is null, so "nobody has said" is
// not an edge case, it is the only case. `unsaid` is therefore a state of its
// own here and on the screen, and it never becomes a `Show` with hours in it.
// What it becomes is a `Show` with blank hours, which the rules module refuses
// in its own words — publication blocked, judgement not made, and the screen
// saying which judgement is not being made and why.
//
// ---------------------------------------------------------------------------
// `Show` HOLDS ONE WINDOW FOR THE WHOLE RUN AND AN ACTIVATION MAY SAY MORE
//
// This is the real mismatch and it is named in 108's own header: "The kiosk
// admin's `Show` is one window applied to every day of the run, and
// `darkRefusals` passes `gapsIn` a single Span. Carrying this across is
// therefore not a copy; `Show` has to grow a per-day reading."
//
// Until it does, there are exactly two honest options for a run whose days
// disagree — a lunch break, a late first evening, an early breakdown day, a day
// marked shut — and both were considered:
//
//   SYNTHESISE ONE WINDOW. The union of the day's windows under-refuses, and
//   the intersection over-refuses. Both produce a dark-screen verdict that is
//   confidently wrong, which is the exact failure 108 was written to end, and
//   neither is visible to the person reading the verdict.
//
//   CALL `refusals()` ONCE PER DAY WITH A ONE-DAY `Show`. Tempting, because
//   every judgement stays the module's and the console does no arithmetic at
//   all. Rejected because the module's own sentences become false: a refusal
//   built against a one-day show says "the show runs 09:00 to 18:00, 2026-03-04
//   to 2026-03-04" about a three-day show, and `darkRefusals` deliberately
//   groups gaps by stretch rather than per day precisely so that "a schedule
//   missing its mornings is one mistake" does not read as three. Calling it per
//   day undoes that on purpose, in a module whose prose is most of its value.
//
// So `varies` makes no `Show` at all. The hours stay blank, the module refuses
// the show in its own words, publication is blocked, and the screen says the
// activation described hours this admin cannot yet be judged against, and shows
// them. A refusal that names the limit is worth more than an approval that
// stepped around it.

import type { ActivationLine, OpeningHours } from "../../../operator/src/activations.js";
import { showDays, type Draft, type Show } from "./admin.js";

/**
 * One fact the activation has not stated.
 *
 * Carried as a value rather than as a sentence so that the screen decides the
 * wording once and this file states no policy about how loudly to say it.
 */
export type UnsaidFact = "name" | "venue" | "time_zone" | "run" | "hours";

export type HoursReading =
  /**
   * Nobody has said. Distinct from shut, and the distinction is the whole of
   * 108 section 4.
   */
  | { readonly kind: "unsaid" }
  /**
   * Every day of the run is open for the same single stretch, which is exactly
   * what a `Show` can hold.
   */
  | { readonly kind: "one_window"; readonly opensAt: string; readonly closesAt: string }
  /**
   * The activation says something a `Show` cannot hold: different hours on
   * different days, a day that shuts for lunch, a day marked shut, or a day of
   * the run nobody has spoken for.
   */
  | { readonly kind: "varies"; readonly days: readonly DayReading[] };

/** What the activation says about one day of the run, echoed rather than judged. */
export type DayReading = {
  readonly on_date: string;
  /** The windows on that day, in the server's order. Empty means shut or unspoken — see `said`. */
  readonly windows: readonly { readonly opens_at: string; readonly closes_at: string }[];
  /** Whether anybody has said anything at all about this day. */
  readonly said: boolean;
};

export type Ground = {
  /**
   * What `refusals()` is given, blanks and all.
   *
   * A blank is not softened here and is not filled in with a default. The rules
   * module has a sentence for every one of them and the console's job is to
   * draw that sentence with its provenance attached, not to pre-empt it.
   */
  readonly venue: Draft["venue"];
  readonly show: Show;
  readonly hours: HoursReading;
  /** Which of the activation's own facts are absent, so the screen can say which. */
  readonly unsaid: readonly UnsaidFact[];
  /** The days of the run, from the rules module's own walk. Empty where the run is unknown or absurd. */
  readonly runDays: readonly string[];
};

/**
 * The activation, as the ground a draft is judged against.
 *
 * Total: any row in, one reading out, no throw. It is called on every render of
 * a screen whose data comes off a network, so a shape it could throw on would be
 * a blank page reached by a server on a different build.
 */
export function groundFor(activation: ActivationLine): Ground {
  const unsaid: UnsaidFact[] = [];

  const timeZone = text(activation.time_zone);
  const venueName = text(activation.venue);
  if (text(activation.name) === "") unsaid.push("name");
  if (venueName === "") unsaid.push("venue");
  if (timeZone === "") unsaid.push("time_zone");

  const opensOn = text(activation.opens_on);
  const closesOn = text(activation.closes_on);
  if (opensOn === "" || closesOn === "") unsaid.push("run");

  // The run is walked by the rules module rather than here. `showDays` is what
  // `darkRefusals` itself walks, so a day this file thinks is in the run and
  // the refusal does not — or the other way — is impossible by construction
  // rather than by two implementations agreeing.
  const { days } = showDays(opensOn, closesOn);
  const hours = readHours(days, activation.opening_hours ?? []);
  if (hours.kind !== "one_window") unsaid.push("hours");

  return {
    venue: { timeZone, name: venueName },
    show: {
      opensOn,
      closesOn,
      // Blank unless the activation described exactly one window for every day
      // of the run. See the header for why nothing is synthesised.
      opensAt: hours.kind === "one_window" ? hours.opensAt : "",
      closesAt: hours.kind === "one_window" ? hours.closesAt : "",
    },
    hours,
    unsaid,
    runDays: days,
  };
}

/**
 * Which of the three states the opening hours are in.
 *
 * String equality and nothing else. There is no arithmetic here at all: a
 * window is compared to another window by comparing the two readings the server
 * sent, so the answer cannot be a minute out in either direction.
 */
function readHours(days: readonly string[], given: readonly OpeningHours[]): HoursReading {
  if (days.length === 0) return { kind: "unsaid" };

  const readings: DayReading[] = [];
  for (const day of days) {
    const rows = given.filter((row) => row.on_date === day);
    readings.push({
      on_date: day,
      windows: rows
        .filter((row) => row.opens_at !== null && row.closes_at !== null)
        .map((row) => ({ opens_at: String(row.opens_at), closes_at: String(row.closes_at) })),
      said: rows.length > 0,
    });
  }

  // Nothing at all has been said about any day of the run. The commonest state
  // by a distance, and the one that must not read as a hall that never opens.
  if (readings.every((day) => !day.said)) return { kind: "unsaid" };

  const first = readings[0]?.windows[0];
  const uniform =
    first !== undefined &&
    readings.every(
      (day) =>
        day.windows.length === 1 &&
        day.windows[0]?.opens_at === first.opens_at &&
        day.windows[0]?.closes_at === first.closes_at,
    );

  return uniform
    ? { kind: "one_window", opensAt: first.opens_at, closesAt: first.closes_at }
    : { kind: "varies", days: readings };
}

const text = (value: string | null | undefined): string => (typeof value === "string" ? value.trim() : "");
